import type { DateTimeLocal } from 'generated/graphql';
import memoize from 'lodash.memoize';
import { env } from 'config/env';
import { pluralize } from './string';
import { isNumber } from './isNumber';
import { formatNameValue } from 'domains/worklist/Worklist/Columns/utils';

/**
 * To convert a date to an input element (not for display directly). If using for display
 * use one of the locale specific ones.
 */
export const convertDateToLocalDateString = (date: Nullable<Date>): string | null => {
  if (date == null) {
    return null;
  }

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Converts a local date string in YYYY-MM-DD format to a Date object in local timezone.
 * This is the inverse of convertDateToLocalDateString.
 *
 * IMPORTANT: This function parses the date string as local time, not UTC.
 * Using new Date(dateString) would interpret it as UTC midnight, causing timezone shifts.
 */
export const convertLocalDateStringToDate = (dateString: Nullable<string>): Date | null => {
  if (dateString == null || !dateString) {
    return null;
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return null;
  }

  const [year, month, day] = dateString.split('-').map(Number);

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  const date = new Date(year, month - 1, day);

  if (isNaN(date.getTime())) {
    return null;
  }

  return date;
};

export const toLocaleString = (
  date?: Date | null,
  options?: Intl.DateTimeFormatOptions
): Nullable<string> =>
  date != null
    ? new Date(date).toLocaleString([], {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        ...options,
      })
    : null;

export const toLocaleTime = (
  date?: Date | null,
  options?: Intl.DateTimeFormatOptions
): Nullable<string> =>
  date != null
    ? new Date(date).toLocaleString([], {
        hour: '2-digit',
        minute: '2-digit',
        ...options,
      })
    : null;

export const toUTCLocaleString = (
  date?: Date | null,
  options?: Intl.DateTimeFormatOptions
): Nullable<string> =>
  toLocaleString(date, {
    timeZone: 'UTC',
    ...options,
  });

export const toUTCLocaleDateTimeString = (
  date?: Date | null,
  options?: Intl.DateTimeFormatOptions
): Nullable<string> =>
  date != null
    ? new Date(date).toLocaleTimeString([], {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
        ...options,
      })
    : null;

// @ts-expect-error [EN-10263] - TS2322 - Type '((dateTimeLocal?: DateTimeLocal | null | undefined, options?: DateTimeFormatOptions | undefined) => string | null | undefined) & MemoizedFunction' is not assignable to type '(dateTimeLocal: DateTimeLocal | Date | null | undefined, options?: DateTimeFormatOptions | undefined) => string | null | undefined'.
export const toLocaleDateTimeStringMemoized: (
  dateTimeLocal: Nullable<DateTimeLocal | Date>,
  options?: Intl.DateTimeFormatOptions
) => Nullable<string> = memoize(
  (
    dateTimeLocal?: DateTimeLocal | null,
    options?: Intl.DateTimeFormatOptions
  ): Nullable<string> => {
    return toLocaleDateTimeString(dateTimeLocal, options);
  },
  (dateTimeLocal) =>
    dateTimeLocal != null
      ? `${dateTimeLocal.year}${dateTimeLocal.month}${dateTimeLocal.date}${dateTimeLocal.hours}${dateTimeLocal.minutes}${dateTimeLocal.seconds}`
      : null
);

export const toLocaleDateTimeString = (
  dateTimeLocal: Nullable<DateTimeLocal | Date>,
  options?: Intl.DateTimeFormatOptions
): Nullable<string> => {
  if (dateTimeLocal == null) {
    return null;
  }

  const date =
    dateTimeLocal instanceof Date
      ? dateTimeLocal
      : new Date(
          dateTimeLocal.year,
          dateTimeLocal.month,
          dateTimeLocal.date,
          dateTimeLocal.hours,
          dateTimeLocal.minutes,
          dateTimeLocal.seconds
        );
  return date.toLocaleTimeString(env.NODE_ENV === 'test' ? 'en-US' : [], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
};

export const toLocaleTimeString = (
  date?: Date | null,
  options?: Intl.DateTimeFormatOptions
): Nullable<string> =>
  date != null
    ? new Date(date).toLocaleTimeString(env.NODE_ENV === 'test' ? 'en-US' : [], {
        hour: '2-digit',
        minute: '2-digit',
        ...options,
      })
    : null;

export const formatDicomDate = (dicomDate: string): string => {
  const date = new Date(`${dicomDate.slice(0, 4)},${dicomDate.slice(4, 6)},${dicomDate.slice(6)}`);
  return toLocaleString(date) ?? '';
};

export const formatDicomTime = (dicomTime: string): string => {
  const time = new Date(
    1,
    1,
    1,
    Number(dicomTime.slice(0, 2)),
    Number(dicomTime.slice(2, 4)),
    Number(dicomTime.slice(4))
  );
  return toLocaleTime(time) ?? '';
};

/**
 * @param age A string of characters with one of the following formats -- nnnD, nnnW, nnnM, nnnY;
 * where nnn shall contain the number of days for D, weeks for W, months for M, or years for Y.
 * Example: "018M" would represent an age of 18 months.
 */
export const formatAgeString = (age: string): string => {
  if (age.length !== 4 || !isNumber(age.slice(0, 3)) || !(age[3] in AGE_PERIODS)) {
    return age; // invalid format, return so we show something
  }
  const num = Number(age.slice(0, 3));
  const period = age[3];
  // @ts-expect-error [EN-10263] - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly D: "day"; readonly W: "week"; readonly M: "month"; readonly Y: "year"; }'.
  return `${num} ${pluralize(num, AGE_PERIODS[period])}`;
};

export const formatReportSignedByLabel = ({
  firstName,
  lastName,
  submittedAt,
}: {
  firstName: Nullable<string>;
  lastName: Nullable<string>;
  submittedAt: Date;
}): string => {
  const formattedName = formatNameValue({ firstName, lastName });
  const formattedDate = toLocaleString(submittedAt);
  const formattedTime = toLocaleTimeString(submittedAt);
  const formatted = formattedDate && formattedTime ? `${formattedDate} at ${formattedTime}` : '';

  return `Signed by ${formattedName}${formatted ? ` on ${formatted}` : ''}`;
};

const AGE_PERIODS = {
  D: 'day',
  W: 'week',
  M: 'month',
  Y: 'year',
} as const;

export const getLocaleDateStringOrNA = (value: Nullable<Date>) => {
  if (value == null) {
    return 'N/A';
  }

  return toLocaleDateTimeString(new Date(value));
};

export const getUTCDateStringOrNA = (value: Nullable<Date>) => {
  if (value == null) {
    return 'N/A';
  }

  return toUTCLocaleString(new Date(value));
};

export function parsePythonDurationToSeconds(duration: Nullable<string>): number {
  if (duration == null || duration.trim() === '') return 0;
  const trimmedDuration = duration.trim();

  const djangoMatch = trimmedDuration.match(/^(?:(\d+)\s+)?(\d{1,2}):(\d{2}):(\d{2})$/);
  if (djangoMatch) {
    const days = Number(djangoMatch[1] ?? 0);
    const hours = Number(djangoMatch[2] ?? 0);
    const minutes = Number(djangoMatch[3] ?? 0);
    const seconds = Number(djangoMatch[4] ?? 0);

    return days * 86400 + hours * 3600 + minutes * 60 + seconds;
  }

  const isoMatch = trimmedDuration.match(/P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (isoMatch) {
    const days = Number(isoMatch[1] ?? 0);
    const hours = Number(isoMatch[2] ?? 0);
    const minutes = Number(isoMatch[3] ?? 0);
    const seconds = Number(isoMatch[4] ?? 0);

    return days * 86400 + hours * 3600 + minutes * 60 + seconds;
  }

  return 0;
}
